import { useEffect, useReducer, useRef } from 'react'
import { smoothingFactor } from '../chart/geometry'

/**
 * Eases a number toward a target so it counts rather than cuts.
 *
 * Deliberately built on `smoothingFactor` from the chart rather than a spring
 * library: the headline and the fan are showing the same simulation, and if they
 * settle on different curves the screen reads as two components that happen to
 * be near each other instead of one instrument responding. One motion system,
 * not two.
 *
 * `motion` was carried as a dependency for a while on the assumption something
 * would want it; nothing ever did, for the reason above, so it has been dropped.
 * If a future feature reaches for a spring, weigh it against this — a second
 * easing curve on the same screen is the cost.
 *
 * `tau` is slightly slower than the fan's 90ms. The number is read, not watched,
 * and a digit that snaps is harder to follow than a shape that does.
 */
export function useRollingNumber(target: number, tau = 140): number {
  const currentRef = useRef<number | null>(null)
  /**
   * Where the running loop reads its destination.
   *
   * It cannot read the `target` argument, and that is the whole of `B-72`. The
   * effect below schedules a frame only when none is in flight, and `step` closes
   * over the `target` of the render that created it — so a value arriving
   * mid-animation neither restarted the loop nor reached the running one. The loop
   * finished easing to the PREVIOUS target, wrote it to `currentRef`, set
   * `frameRef` back to null and stopped, and nothing woke it again because
   * `target` had not changed since the effect last ran. Whatever was in flight
   * when it converged is what stayed on screen, so dragging a slider through 37 →
   * 50 → 37 showed a different number at each visit to 37 — measured over CDP
   * against the built bundle at 30% / 21% / 7%, while the `sr-only` span beside
   * it, which renders the computed value rather than the eased one, read 29%
   * every time.
   *
   * The original report quoted that accessible span as 29% / 0% / 29%. It cannot
   * actually vary: the engine runs on a fixed seed and all three reads are the
   * same age on the same plan. The 0% was `Headline`'s `?? 0` standing in while
   * the worker had not yet replied — an artefact of when the reading was taken,
   * not a second wrong number.
   *
   * `useAnimatedFan` has the identical loop shape and never had the bug, for
   * exactly this reason: it mutates `stateRef.current.target` and its `step` reads
   * through the ref.
   *
   * **This covers `target` and nothing else.** `tau` is still resolved through the
   * effect closure two dozen lines down, so a caller that changed it mid-animation
   * would hit a variant of this same bug. Nothing does: `Headline` is the only call
   * site and passes no `tau`. If a second caller ever wants one, it needs the same
   * treatment as this ref, not a fresh diagnosis.
   */
  const targetRef = useRef(target)
  const frameRef = useRef<number | null>(null)
  const lastTimeRef = useRef(0)
  const [, forceRender] = useReducer((c: number) => c + 1, 0)

  /*
   * The first value this hook is handed arrives fully formed: it never eases up
   * from zero to whatever it was given first.
   *
   * **WHAT THAT DOES NOT BUY, because the note here used to claim it did.** The
   * point of seeding was that nobody watches "a sequence of wrong numbers about
   * their own retirement" on load. At the only call site, they still do.
   * `App.tsx` renders `Headline` with no gate on the result — the one branch
   * there covers an error, not a pending run — and `Headline` maps a null result
   * to `0`. So the first value reaching this hook is a placeholder zero, first
   * paint is a large `0%`, and the real probability arrives a frame later and is
   * eased up to over roughly 68 frames.
   *
   * **That roll-up is intended, and this note is the only place that says so.**
   * Raised at MAINT-06's review and settled there: counting up from zero on load
   * is acceptable, and the behaviour stays. So the original objection above is
   * overruled rather than outstanding — do not "fix" the zero by gating the
   * headline on a pending result without reopening that decision first.
   *
   * What the seeding still buys, which is why the line below stays: a value
   * arriving into an already-mounted hook is eased to, while the FIRST value any
   * given mount receives is taken as-is. Without it every remount would animate
   * from zero again. `useRollingNumber.dom.test.ts` pins both — the seeding, and
   * the roll-up exactly as it occurs — so neither can change silently.
   */
  if (currentRef.current === null) currentRef.current = target

  useEffect(() => {
    // Publish the new destination before deciding whether to wake the loop. A
    // frame already in flight picks this up on its next tick; the branch below
    // only has to cover the case where nothing is running.
    targetRef.current = target

    if (frameRef.current === null) {
      lastTimeRef.current = 0
      frameRef.current = requestAnimationFrame(step)
    }

    function step(time: number): void {
      const current = currentRef.current
      if (current === null) {
        frameRef.current = null
        return
      }

      const deltaMs =
        lastTimeRef.current === 0 ? 16.7 : Math.min(64, time - lastTimeRef.current)
      lastTimeRef.current = time

      // Read once per frame: the loop stops on the latest target, never on the
      // one it happened to start with.
      const latest = targetRef.current
      const delta = latest - current
      // Epsilon is scaled to the value so this works for a 0-1 probability and
      // for a crore-sized corpus without a per-caller tuning knob.
      const epsilon = Math.max(1e-4, Math.abs(latest) * 1e-4)

      if (Math.abs(delta) <= epsilon) {
        currentRef.current = latest
        frameRef.current = null
        forceRender()
        return
      }

      currentRef.current = current + delta * smoothingFactor(deltaMs, tau)
      forceRender()
      frameRef.current = requestAnimationFrame(step)
    }
  }, [target, tau])

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    },
    [],
  )

  return currentRef.current
}
